/**
 * WebCodecs Video Encoder - Modern high-performance video export
 * 
 * This service uses the WebCodecs API for efficient client-side video processing
 * and encoding, providing professional export capabilities with progress tracking.
 */

class VideoEncoder {
  constructor() {
    this.isSupported = this._checkWebCodecsSupport();
    this.activeEncodings = new Map();
    this.canvas = null;
    this.ctx = null;
    this._initializeCanvas();
  }

  _checkWebCodecsSupport() {
    return (
      'VideoEncoder' in window &&
      'VideoDecoder' in window &&
      'VideoFrame' in window &&
      'EncodedVideoChunk' in window
    );
  }

  _initializeCanvas() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
  }

  /**
   * Export timeline composition to video file
   */
  async exportTimeline(timeline, options = {}) {
    if (!this.isSupported) {
      throw new Error('WebCodecs not supported. Falling back to FFmpeg export.');
    }

    const exportId = `export_${Date.now()}`;
    const config = this._buildExportConfig(options);
    
    try {
      this.activeEncodings.set(exportId, {
        status: 'initializing',
        progress: 0,
        startTime: Date.now()
      });

      // Step 1: Prepare video sources
      const videoSources = await this._prepareVideoSources(timeline.clips);
      
      // Step 2: Set up encoder
      const encoder = await this._createEncoder(config);
      
      // Step 3: Process timeline composition
      const videoBlob = await this._processTimelineComposition(
        videoSources, 
        timeline, 
        encoder, 
        config,
        exportId
      );

      this._updateProgress(exportId, 100, 'completed');
      
      return {
        blob: videoBlob,
        duration: timeline.duration,
        format: config.format,
        quality: config.quality,
        size: videoBlob.size
      };

    } catch (error) {
      this._updateProgress(exportId, 0, 'failed', error.message);
      throw error;
    } finally {
      this.activeEncodings.delete(exportId);
    }
  }

  /**
   * Build export configuration
   */
  _buildExportConfig(options) {
    const defaults = {
      width: 1920,
      height: 1080,
      framerate: 30,
      bitrate: 5000000, // 5 Mbps
      format: 'mp4',
      codec: 'avc1.42E01E', // H.264 baseline
      quality: 'high',
      aspectRatio: '16:9'
    };

    const config = { ...defaults, ...options };

    // Adjust dimensions based on aspect ratio
    if (config.aspectRatio === '9:16') {
      config.width = 1080;
      config.height = 1920;
    }

    // Adjust quality settings
    switch (config.quality) {
      case 'low':
        config.bitrate = 1000000; // 1 Mbps
        break;
      case 'medium':
        config.bitrate = 3000000; // 3 Mbps
        break;
      case 'high':
        config.bitrate = 5000000; // 5 Mbps
        break;
      case 'ultra':
        config.bitrate = 8000000; // 8 Mbps
        config.framerate = 60;
        break;
    }

    return config;
  }

  /**
   * Prepare video sources for processing
   */
  async _prepareVideoSources(clips) {
    const videoSources = new Map();
    
    for (const clip of clips) {
      try {
        const video = document.createElement('video');
        video.crossOrigin = 'anonymous';
        video.preload = 'metadata';
        
        await new Promise((resolve, reject) => {
          video.onloadedmetadata = resolve;
          video.onerror = reject;
          video.src = clip.url;
        });

        videoSources.set(clip.id, {
          video,
          duration: clip.duration,
          startTime: clip.startTime,
          endTime: clip.endTime
        });

      } catch (error) {
        console.warn(`Failed to load video for clip ${clip.id}:`, error);
        // Continue with other clips
      }
    }

    return videoSources;
  }

  /**
   * Create and configure video encoder
   */
  async _createEncoder(config) {
    const encoderConfig = {
      codec: config.codec,
      width: config.width,
      height: config.height,
      bitrate: config.bitrate,
      framerate: config.framerate,
      keyFrameInterval: config.framerate * 2, // Keyframe every 2 seconds
      realtime: false,
      alpha: 'discard',
      latencyMode: 'quality',
      bitrateMode: 'variable'
    };

    // Check codec support
    const codecSupport = await VideoEncoder.isConfigSupported(encoderConfig);
    if (!codecSupport.supported) {
      throw new Error(`Codec ${config.codec} not supported`);
    }

    const chunks = [];
    const encoder = new VideoEncoder({
      output: (chunk, metadata) => {
        chunks.push(new Uint8Array(chunk.byteLength));
        chunk.copyTo(chunks[chunks.length - 1]);
      },
      error: (error) => {
        console.error('Encoder error:', error);
        throw error;
      }
    });

    encoder.configure(encoderConfig);
    encoder.chunks = chunks; // Store chunks reference

    return encoder;
  }

  /**
   * Process timeline composition frame by frame
   */
  async _processTimelineComposition(videoSources, timeline, encoder, config, exportId) {
    const { width, height, framerate } = config;
    const frameDuration = 1000000 / framerate; // microseconds per frame
    const totalFrames = Math.ceil(timeline.duration * framerate);
    
    this.canvas.width = width;
    this.canvas.height = height;

    let frameIndex = 0;
    
    for (let time = 0; time < timeline.duration; time += 1 / framerate) {
      // Find active clip at current time
      const activeClip = timeline.clips.find(clip => 
        time >= clip.startTime && time < clip.endTime
      );

      if (activeClip) {
        const videoSource = videoSources.get(activeClip.id);
        if (videoSource) {
          // Calculate time within the clip
          const clipTime = time - activeClip.startTime;
          
          // Seek video to correct time
          if (Math.abs(videoSource.video.currentTime - clipTime) > 0.1) {
            videoSource.video.currentTime = clipTime;
            await new Promise(resolve => {
              videoSource.video.onseeked = resolve;
            });
          }

          // Draw frame to canvas
          this._drawVideoFrame(videoSource.video, width, height);
        } else {
          // Draw black frame if clip not available
          this._drawBlackFrame(width, height);
        }
      } else {
        // Draw black frame for gaps
        this._drawBlackFrame(width, height);
      }

      // Create video frame from canvas
      if (!window.VideoFrame) {
        throw new Error('VideoFrame API not supported');
      }
      const frame = new window.VideoFrame(this.canvas, {
        timestamp: frameIndex * frameDuration,
        duration: frameDuration
      });

      // Encode frame
      encoder.encode(frame, { keyFrame: frameIndex % (framerate * 2) === 0 });
      frame.close();

      frameIndex++;

      // Update progress
      const progress = (frameIndex / totalFrames) * 90; // Reserve 10% for finalization
      this._updateProgress(exportId, progress, 'encoding');

      // Yield control periodically to prevent blocking
      if (frameIndex % 10 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    // Finalize encoding
    await encoder.flush();
    encoder.close();

    this._updateProgress(exportId, 95, 'finalizing');

    // Create video blob
    const videoBlob = await this._createVideoBlob(encoder.chunks, config);
    
    return videoBlob;
  }

  /**
   * Draw video frame to canvas with proper scaling
   */
  _drawVideoFrame(video, targetWidth, targetHeight) {
    const { videoWidth, videoHeight } = video;
    
    // Calculate scaling to maintain aspect ratio
    const scale = Math.min(targetWidth / videoWidth, targetHeight / videoHeight);
    const scaledWidth = videoWidth * scale;
    const scaledHeight = videoHeight * scale;
    
    // Center the video
    const x = (targetWidth - scaledWidth) / 2;
    const y = (targetHeight - scaledHeight) / 2;
    
    // Clear canvas and draw black background
    this.ctx.fillStyle = '#000000';
    this.ctx.fillRect(0, 0, targetWidth, targetHeight);
    
    // Draw video frame
    this.ctx.drawImage(video, x, y, scaledWidth, scaledHeight);
  }

  /**
   * Draw black frame
   */
  _drawBlackFrame(width, height) {
    this.ctx.fillStyle = '#000000';
    this.ctx.fillRect(0, 0, width, height);
  }

  /**
   * Create video blob from encoded chunks
   */
  async _createVideoBlob(chunks, config) {
    // For WebCodecs, we need to wrap the encoded data in a container format
    // This is a simplified implementation - in production, you'd use a proper MP4 muxer
    
    if (config.format === 'webm') {
      return new Blob(chunks, { type: 'video/webm' });
    } else {
      // For MP4, we'll need a more sophisticated approach
      // For now, create a blob that can be processed by the backend
      const metadata = {
        format: config.format,
        codec: config.codec,
        width: config.width,
        height: config.height,
        framerate: config.framerate,
        duration: chunks.length / config.framerate
      };

      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      const videoData = new Blob(chunks, { type: 'application/octet-stream' });
      
      return new Blob([metadataBlob, videoData], { type: 'video/mp4' });
    }
  }

  /**
   * Fallback export using canvas recording (for unsupported browsers)
   */
  async _fallbackExport(timeline, options = {}) {
    const config = this._buildExportConfig(options);
    const stream = this.canvas.captureStream(config.framerate);
    
    const recorder = new MediaRecorder(stream, {
      mimeType: 'video/webm;codecs=vp9',
      videoBitsPerSecond: config.bitrate
    });

    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    return new Promise((resolve, reject) => {
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        resolve(blob);
      };

      recorder.onerror = reject;

      // Start recording and play timeline
      recorder.start();
      
      // TODO: Implement timeline playback for recording
      // This would require synchronizing timeline playback with recording
      
      // For now, stop after estimated duration
      setTimeout(() => {
        recorder.stop();
      }, timeline.duration * 1000);
    });
  }

  /**
   * Export with automatic quality optimization
   */
  async exportWithOptimization(timeline, targetSizeMB = 50) {
    const qualities = ['medium', 'high', 'low', 'ultra'];
    
    for (const quality of qualities) {
      try {
        const result = await this.exportTimeline(timeline, { quality });
        const sizeMB = result.blob.size / (1024 * 1024);
        
        if (sizeMB <= targetSizeMB || quality === 'low') {
          return {
            ...result,
            optimizedForSize: sizeMB <= targetSizeMB,
            finalSizeMB: sizeMB,
            qualityUsed: quality
          };
        }
      } catch (error) {
        console.warn(`Export failed with quality ${quality}:`, error);
        continue;
      }
    }
    
    throw new Error('Failed to export video with any quality setting');
  }

  /**
   * Get export progress
   */
  getExportProgress(exportId) {
    return this.activeEncodings.get(exportId) || null;
  }

  /**
   * Cancel active export
   */
  cancelExport(exportId) {
    const encoding = this.activeEncodings.get(exportId);
    if (encoding) {
      encoding.status = 'cancelled';
      this.activeEncodings.delete(exportId);
      return true;
    }
    return false;
  }

  /**
   * Update export progress
   */
  _updateProgress(exportId, progress, status, error = null) {
    const encoding = this.activeEncodings.get(exportId);
    if (encoding) {
      encoding.progress = Math.round(progress);
      encoding.status = status;
      encoding.error = error;
      encoding.lastUpdate = Date.now();
    }
  }

  /**
   * Get supported export formats
   */
  async getSupportedFormats() {
    const formats = [];
    
    if (this.isSupported) {
      // Test common codecs
      const codecs = [
        { name: 'H.264 (MP4)', codec: 'avc1.42E01E', format: 'mp4' },
        { name: 'H.265 (MP4)', codec: 'hev1.1.6.L93.B0', format: 'mp4' },
        { name: 'VP9 (WebM)', codec: 'vp09.00.10.08', format: 'webm' },
        { name: 'AV1 (WebM)', codec: 'av01.0.04M.08', format: 'webm' }
      ];

      for (const { name, codec, format } of codecs) {
        try {
          const support = await VideoEncoder.isConfigSupported({
            codec,
            width: 1920,
            height: 1080,
            bitrate: 5000000,
            framerate: 30
          });

          if (support.supported) {
            formats.push({ name, codec, format, supported: true });
          }
        } catch (error) {
          // Codec not supported
        }
      }
    }

    // Always support fallback formats
    formats.push({ 
      name: 'WebM (Fallback)', 
      codec: 'vp8', 
      format: 'webm', 
      supported: true, 
      fallback: true 
    });

    return formats;
  }

  /**
   * Estimate export time based on timeline duration and quality
   */
  estimateExportTime(timelineDuration, quality = 'high') {
    const baseMultiplier = {
      'low': 0.5,
      'medium': 1.0,
      'high': 2.0,
      'ultra': 4.0
    };

    const multiplier = baseMultiplier[quality] || 1.0;
    
    // Rough estimation: 1 second of video takes 0.5-4 seconds to encode
    return Math.round(timelineDuration * multiplier);
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Cancel all active exports
    for (const exportId of this.activeEncodings.keys()) {
      this.cancelExport(exportId);
    }

    // Clean up canvas
    if (this.canvas) {
      this.canvas.width = 1;
      this.canvas.height = 1;
    }
  }
}

// Export singleton instance
const videoEncoder = new VideoEncoder();
export default videoEncoder;